import {useCallback, useState} from 'react'

export type SortDir = 'asc' | 'desc'

// Lightweight client-side column sorting for report tables. Click a header to
// sort by that key; click again to flip direction. Numeric values sort
// numerically, everything else as a (numeric-aware) string.
export function useTableSort<T extends Record<string, any>>(defaultKey: string, defaultDir: SortDir = 'desc') {
  const [sortKey, setSortKey] = useState<string>(defaultKey)
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir)

  const onSort = useCallback((key: string) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }, [sortKey])

  const sortRows = useCallback(<R extends T>(rows: R[]): R[] => {
    if (!sortKey) return rows // no column chosen -> keep the incoming order
    const dir = sortDir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv), undefined, {numeric: true}) * dir
    })
  }, [sortKey, sortDir])

  // Arrow indicator to append to a header label.
  const arrow = useCallback(
    (key: string) => (sortKey !== key ? ' ↕' : sortDir === 'asc' ? ' ↑' : ' ↓'),
    [sortKey, sortDir]
  )

  return {sortKey, sortDir, onSort, sortRows, arrow}
}
